import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Typography, Box, Button, CircularProgress, Alert, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import Home from "./components/Home";
import { DeviceInfo } from './types';
import "./App.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

function App() {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ""}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ""}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }}
    >
      <BrowserRouter>
        <div className="app-container dark-theme">
          <header className="app-header">
            <nav>
              <UserProfile />
            </nav>
          </header>
          <main>
            <Routes>
              <Route path="/" element={<HomeWithDeviceInfo />} />
            </Routes>
          </main>
        </div>
      </BrowserRouter>
    </Auth0Provider>
  );
}

function PaymentForm({ clientSecret, onSuccess, onCancel }: { 
  clientSecret: string; 
  onSuccess: () => void; 
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message || 'An error occurred');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log("Payment succeeded, calling onSuccess");
        onSuccess(); // This will trigger handlePaymentSuccess which calls /api/play
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={processing}>
          {processing ? 'Processing...' : 'Pay Now'}
        </Button>
      </Box>
    </form>
  );
}

function HomeWithDeviceInfo() {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  console.log('HomeWithDeviceInfo mounted, deviceId:', deviceId, 'searchParams:', searchParams.toString());

  useEffect(() => {
    const fetchDeviceInfo = async () => {
      console.log('fetchDeviceInfo called, deviceId:', deviceId);
      
      if (!deviceId) {
        console.log('No device ID provided, returning early');
        return;
      }

      console.log('Fetching device info for deviceId:', deviceId);
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/device/${deviceId}`);
        console.log('Device API response:', response);
        if (!response.ok) {
          throw new Error('Failed to fetch device information');
        }
        const data = await response.json();
        console.log('Device data received:', data);
        setDeviceInfo(data);
      } catch (err) {
        console.error('Error fetching device info:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceInfo();
  }, [deviceId]);

  const handleFreePlay = useCallback(async () => {
    if (!deviceInfo || !deviceInfo.free_to_play) return;

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          device_id: deviceId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start game');
      }

      const data = await response.json();
      setDeviceInfo(prev => prev ? { ...prev, is_playing: data.is_playing } : null);
      console.log("Free game started successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred starting the game');
    } finally {
      setLoading(false);
    }
  }, [deviceInfo, deviceId, getAccessTokenSilently]);

  const handlePaidPlay = useCallback(async () => {
    if (!deviceInfo || deviceInfo.free_to_play) return;
    setShowPaymentDialog(true);
  }, [deviceInfo]);

  const handlePaymentSuccess = useCallback(async () => {
    if (!deviceInfo) return;

    console.log("Payment succeeded, starting game...");
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      console.log("Calling play endpoint...");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          device_id: deviceId,
          price: deviceInfo.cost_per_play // Include the actual price paid
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start game');
      }

      const data = await response.json();
      setDeviceInfo(prev => prev ? { ...prev, is_playing: data.is_playing } : null);
      console.log("Paid game started successfully");
      setShowPaymentDialog(false); // Close the payment dialog after successful game start
    } catch (err) {
      console.error("Error starting game:", err);
      setError(err instanceof Error ? err.message : 'An error occurred starting the game');
    } finally {
      setLoading(false);
    }
  }, [deviceInfo, deviceId, getAccessTokenSilently]);

  const initiatePayment = useCallback(async () => {
    if (!deviceInfo || deviceInfo.free_to_play || clientSecret) return;

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: deviceInfo.cost_per_play // Already in cents from backend
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret: secret } = await response.json();
      setClientSecret(secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during payment');
    } finally {
      setLoading(false);
    }
  }, [deviceInfo, getAccessTokenSilently, clientSecret]);

  useEffect(() => {
    if (showPaymentDialog && !clientSecret && !loading) {
      initiatePayment();
    }
  }, [showPaymentDialog, clientSecret, loading, initiatePayment]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Home 
        deviceInfo={deviceInfo}
        loading={loading}
        error={error}
        onFreePlay={handleFreePlay}
        onPaidPlay={handlePaidPlay}
      />
      <Dialog 
        open={showPaymentDialog} 
        onClose={() => setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This game requires a payment of ${deviceInfo?.cost_per_play ? (deviceInfo.cost_per_play / 100).toFixed(2) : '0.00'} to play.
          </Typography>
          {clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm 
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowPaymentDialog(false)}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function UserProfile() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const syncUser = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          await fetch(`${process.env.REACT_APP_API_URL}/api/user`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (error) {
          console.error('Failed to sync user:', error);
        }
      }
    };

    syncUser();
  }, [isAuthenticated, getAccessTokenSilently]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return null; // Login button will be rendered in the main content
  }

  return (
    <div className="user-profile">
      <img src={user?.picture} alt={user?.name} className="profile-pic" />
      <span className="user-name">{user?.name}</span>
      <button 
        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        className="logout-button"
      >
        Logout
      </button>
    </div>
  );
}

export default App; 